import React, { ReactElement } from 'react'
import { useTranslation } from 'next-i18next'
import { Outfit } from 'next/font/google'
import { twMerge } from 'tailwind-merge'
import Head from 'next/dist/shared/lib/head'
import { FAVICON_LINK_KEYS } from 'shared/constants/favicon'
import { DomainAssetsInterface } from 'shared/types/domain'
import { isSioDomain } from 'shared/utils/is-sio-domain'
import AuthenticationHeader from 'modules/header/authentication-header'
import { defaultBrandThemes } from 'modules/white-label/constants/brand-settings'
import { useBrand } from 'modules/white-label/contexts/brand-context'

const outfitFont = Outfit({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
  variable: '--font-outfit',
  display: 'swap',
})

interface BaseAuthenticationLayoutProps {
  domainAssets?: DomainAssetsInterface
  hasCustomDomain?: boolean
}

type AuthenticationLayoutProps =
  | ({ titleKey: string; title?: string } & BaseAuthenticationLayoutProps)
  | ({ title: string; titleKey?: string } & BaseAuthenticationLayoutProps)

export function AuthenticationLayout({
  children,
  titleKey,
  title,
  domainAssets,
  hasCustomDomain = false,
}: React.PropsWithChildren<AuthenticationLayoutProps>) {
  // TODO: Uncomment when this task has been done: https://app.shortcut.com/systeme/story/74358/difference-language-frontend-with-backend-in-auth-pages
  // useSyncDashboardLocale()
  const { t } = useTranslation()
  const pageTitle = titleKey ? t(titleKey) : title

  const { brand } = useBrand()

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
      </Head>
      {hasCustomDomain ? (
        <div className="min-h-screen bg-auth-gradient">
          <AuthenticationHeader
            languageSwitcherClassName={'!text-text-primary'}
            logoSrc={domainAssets?.logoUrl || brand.logo?.path}
          />
          <div className="overflow-hidden">
            <div className="mx-auto max-w-screen-2xl px-5 pb-4 lg:pb-9">{children}</div>
          </div>
        </div>
      ) : (
        <div
          className={twMerge(
            outfitFont.variable,
            outfitFont.className,
            'flex min-h-screen flex-col bg-neutral-200 font-outfit',
          )}
        >
          <AuthenticationHeader
            className="absolute inset-x-0 font-outfit"
            buttonLanguageSwitcherClassName="text-base xl:text-lg font-semibold font-outfit"
            logoSrc={
              isSioDomain()
                ? defaultBrandThemes['classic'].logo?.path
                : domainAssets?.logoUrl || brand.logo?.path
            }
          />
          <div className="flex flex-1 items-center justify-center p-4 py-20 md:py-28">
            <div
              className={twMerge(
                'mx-auto flex w-full max-w-[500px] flex-col gap-y-6 overflow-hidden rounded-xl bg-neutral-100 p-6 font-outfit text-neutral-300 md:gap-y-10 md:p-10 xl:w-2/5 xl:max-w-[600px] 2xl:p-12',
              )}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export const getAuthenticationLayout = (page: ReactElement, props: AuthenticationLayoutProps) => (
  <AuthenticationLayout {...props}>{page}</AuthenticationLayout>
)
